import {
  Dropdown,
  DropdownDivider,
  DropdownHeader,
  DropdownItem,
  Navbar,
  NavbarBrand,
} from "flowbite-react";
import { HiViewGrid, HiMenu, HiX, HiHome } from "react-icons/hi";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, type ReactNode } from "react";
import { useSidebar } from "../../context/SidebarContext.tsx";
import { useAuthentication } from "../../auth/useAuthentication.ts";
import { apiClient } from "../../auth/apiClient.ts";
import InlineLanguageSwitcher from "../InlineLanguageSwitcher.tsx";
import { useTranslation } from "../../hooks/useTranslation.ts";
import { getANPHost } from "../../utils/host.ts";
import ProtectedDropdownItem from "../ProtectedDropdownItem.tsx";
import type { ProductInfo } from "../ProtectedDropdownItem.tsx";
import SubscriptionExpiryBadge, {
  DEFAULT_SUBSCRIPTION_PERMISSION,
} from "../SubscriptionExpiryBadge.tsx";

const REQUIRED_PERMISSIONS = ["Factor_investing_advanced", "Factor_investing_basic"];

// Flowbite's Dropdown positions its menu with `position: absolute` in place
// (no portal), so it only stacks above other content via z-index. Its default
// `z-10` ties with page content like sticky panel footers, and DOM order then
// decides the winner. Bump it above the app's highest in-page z-index (`z-50`
// modals) so the navbar menus always render on top, matching the precedent in
// AccountSelector.tsx.
const DROPDOWN_THEME = { floating: { base: "z-[9999]" } };

// Row/column spacing for the navbar items. The logo column has a zero flex
// basis (`flex-1 min-w-0`), so it never forces the icons onto a new line on
// phones - it shrinks instead. Only the signed-out buttons wrap below.
const NAVBAR_THEME = { root: { inner: { base: "gap-2" } } };

export interface MainNavbarProps {
  brandName?: string;
  brandLogo?: string;
  brandHref?: string;
  products?: ProductInfo[];
  /** Where the avatar menu's "Profile" item points. Defaults to the shared
   * ANP profile page; apps that host their own (e.g. factor-investing's
   * /profile route) pass a local path instead. */
  profileHref?: string;
  renderExtraMenuItems?: () => ReactNode;
  renderModals?: () => ReactNode;
  /** Opens the subscription upgrade modal when the expiry badge is clicked. */
  onSubscriptionBadgeClick?: () => void;
}

const MainNavbar = ({
  brandName = "Analytical Platform",
  brandLogo = "/AP_StockCharacteristics_FINAL_light-20260917-130250.svg",
  brandHref = "/",
  profileHref,
  renderExtraMenuItems,
  renderModals,
  products = [],
  onSubscriptionBadgeClick,
}: MainNavbarProps) => {
  const location = useLocation();
  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const { user, logOut, isLoading } = useAuthentication();
  const { t, tCommon } = useTranslation();
  const navigate = useNavigate();
  const host = getANPHost();

  // Generate user initials from name
  const getUserInitials = (name: string): string => {
    const nameParts = name.split(" ");
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Determine if current page should show sidebar toggle (currently only Overview page)
  const shouldShowSidebarToggle = location.pathname === "/";

  // The auth pages already are the sign-in flow - the signed-out CTA buttons
  // only add clutter there (especially on small screens)
  const isAuthPage = ["/login", "/signup", "/forgot-password"].includes(location.pathname);

  // Handle logout
  const handleLogout = () => {
    logOut();
  };

  const handleLogin = () => {
    navigate("/login");
  };

  const hasRequiredPermission =
    user?.permissions.some(permission => REQUIRED_PERMISSIONS.includes(permission)) ?? false;

  // Where a signed-in user without the factor-investing permission belongs:
  // /subscriptions when they have no active subscription at all (nothing to
  // show them on /welcome yet), /welcome when a subscription record exists but
  // the permission hasn't landed yet (e.g. mid-provisioning). A failed lookup
  // falls back to /welcome rather than leaving them stuck.
  const resolveFallbackRoute = useCallback(
    () =>
      apiClient
        .getActiveSubscription(DEFAULT_SUBSCRIPTION_PERMISSION)
        .then(({ data }) => (data.results.length > 0 ? "/welcome" : "/subscriptions"))
        .catch(() => "/welcome"),
    []
  );

  // Pages a signed-in user reaches regardless of entitlement: the two landing
  // pages the guard itself redirects to, plus the account settings page - users
  // without a subscription still need to manage their account. The profile page
  // only counts when this app hosts it locally (apps pointing profileHref at the
  // shared ANP profile navigate away anyway).
  const alwaysAllowedPaths = ["/welcome", "/subscriptions"];
  if (profileHref?.startsWith("/")) {
    alwaysAllowedPaths.push(profileHref);
  }
  const isAlwaysAllowedPath = alwaysAllowedPaths.includes(location.pathname);

  // Check for required permissions and redirect if missing.
  useEffect(() => {
    // Only check when the user is loaded and not already on an allowed page
    if (isLoading || !user || isAlwaysAllowedPath) {
      return;
    }

    if (hasRequiredPermission) {
      return;
    }

    let cancelled = false;

    resolveFallbackRoute().then(route => {
      if (!cancelled) {
        navigate(route);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [user, isLoading, hasRequiredPermission, isAlwaysAllowedPath, navigate, resolveFallbackRoute]);

  // Authenticated users go to the dashboard; signed-out users go to
  // /subscriptions (public since CRT-2127). If an authenticated user lacks
  // the required permission, the entitlement guard effect above fires right
  // after (location changes to "/", which isn't an always-allowed path) and
  // redirects them onward, so this doesn't need to duplicate that check.
  const handleHomeClick = () => {
    navigate(user ? "/" : "/subscriptions");
  };

  return (
    <Navbar fluid rounded={false} theme={NAVBAR_THEME}>
      <div className="flex min-w-0 flex-1 items-center">
        {/* Sidebar Toggle/Close Button - visible only when enabled and on small screens */}
        {shouldShowSidebarToggle && (
          <button
            onClick={toggleSidebar}
            className="mr-3 p-2 text-gray-500 rounded-lg hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 lg:hidden"
          >
            {isSidebarOpen ? <HiX size={20} /> : <HiMenu size={20} />}
          </button>
        )}

        <NavbarBrand href={brandHref} className="min-w-0">
          <img
            src={brandLogo}
            className="h-auto max-h-6 w-auto max-w-full sm:mr-3 sm:max-h-9"
            alt={`${brandName} Logo`}
          />
        </NavbarBrand>
      </div>

      <div className="flex shrink-0 items-center gap-1 sm:gap-3 md:order-2">
        {/* Subscription expiry countdown (hidden when logged out / no subscription) */}
        {user && (
          <SubscriptionExpiryBadge onClick={onSubscriptionBadgeClick} />
        )}

        {/* Home Icon */}
        <button
          onClick={handleHomeClick}
          className="p-2 text-gray-500 rounded-lg hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 cursor-pointer"
        >
          <HiHome size={20} />
        </button>

        {/* Four Tiles Icon with Dropdown */}
        <Dropdown
          arrowIcon={false}
          inline
          theme={DROPDOWN_THEME}
          label={
            <button className="p-2 text-gray-500 rounded-lg hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 cursor-pointer">
              <HiViewGrid size={20} />
            </button>
          }
        >
          <DropdownHeader>
            <span className="block text-sm font-semibold">
              {t("navbar.analyticalTools")}
            </span>
          </DropdownHeader>
          <ProtectedDropdownItem
            productName="Stock Characteristics"
            translationKey="indicatorInvesting"
            products={products}
            onClick={() => navigate("/")}
          >
            {t("navbar.stockCharacteristics")}
          </ProtectedDropdownItem>
          <ProtectedDropdownItem
            productName="Portfolio Manager"
            translationKey="portfolioManager"
            products={products}
            href={`${host}/optimization-fe/portfolioManager`}
          >
            {t("navbar.portfolioManager")}
          </ProtectedDropdownItem>
          <ProtectedDropdownItem
            productName="Market Sentiment"
            translationKey="marketSentiment"
            products={products}
            href={`https://sfa.analyticalplatform.com/#/feed`}
          >
            {t("navbar.marketSentiment")}
          </ProtectedDropdownItem>
          <ProtectedDropdownItem
            productName="Investment Strategies"
            translationKey="investmentStrategies"
            products={products}
            href={`${host}/stockPickingLab`}
          >
            {t("navbar.investmentStrategies")}
          </ProtectedDropdownItem>
          <ProtectedDropdownItem
            productName="Pattern Trading"
            translationKey="patternTrading"
            products={products}
            href={`${host}/patternLab`}
          >
            {t("navbar.patternTrading")}
          </ProtectedDropdownItem>
        </Dropdown>

        {/* User Menu (the signed-out buttons live in their own row below) */}
        {(user || isLoading) && (
          <Dropdown
            arrowIcon={false}
            inline
            theme={DROPDOWN_THEME}
            label={
              isLoading ? (
                <div className="flex items-center justify-center w-8 h-8 bg-gray-300 rounded-full animate-pulse">
                  <div className="w-4 h-4 bg-gray-400 rounded-full animate-pulse"></div>
                </div>
              ) : (
                <div className="flex items-center justify-center w-8 h-8 text-sm font-medium text-white bg-gradient-to-br from-blue-600 to-blue-700 rounded-full hover:from-blue-700 hover:to-blue-800 transition-all duration-200 cursor-pointer shadow-md hover:shadow-lg">
                  {user ? getUserInitials(user.name) : "U"}
                </div>
              )
            }
          >
            <DropdownHeader>
              <span className="block text-sm">
                {isLoading ? t("navbar.loading") : user?.name || t("navbar.anonymousUser")}
              </span>
              <span className="block truncate text-sm font-medium">
                {isLoading ? t("navbar.loading") : user?.email || t("navbar.noEmailProvided")}
              </span>
            </DropdownHeader>
            <DropdownItem href={profileHref ?? `${host}/profile`}>{tCommon("profile")}</DropdownItem>
            <DropdownItem href="/">{tCommon("dashboard")}</DropdownItem>
            <DropdownItem onClick={() => window.open("https://meetings-eu1.hubspot.com/analytical-platform?uuid=b7d0a19d-d4d1-4bf2-96b8-9665f83d713f", "_blank", "noopener,noreferrer")}>{tCommon("scheduleMeeting")}</DropdownItem>
            {/* App-specific extra menu items (e.g., Stripe portal, RoboMarkets, Product Tour) */}
            {renderExtraMenuItems?.()}
            <DropdownDivider />
            <DropdownItem>
              <div className="w-full">
                <InlineLanguageSwitcher />
              </div>
            </DropdownItem>
            <DropdownDivider />
            {user && (
              <DropdownItem onClick={handleLogout}>
                {tCommon("signOut")}
              </DropdownItem>
            )}
            {!user && (
              <DropdownItem onClick={() => handleLogin()}>
                {tCommon("signIn")}
              </DropdownItem>
            )}
          </Dropdown>
        )}
      </div>

      {/* Signed-out buttons: a full-width row of their own on phones so they
          never squeeze the logo, inline after the icons from `sm` up */}
      {!user && !isLoading && !isAuthPage && (
        <div className="flex w-full items-center justify-end gap-2 sm:w-auto md:order-3">
          <button
            onClick={handleLogin}
            className="px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 transition-all duration-200 border hover:shadow-md hover:scale-105 cursor-pointer"
            style={{
              backgroundColor: "#EBF5FF",
              borderColor: "#A4CAFE",
              color: "#1A55DB",
            }}
          >
            {tCommon("signIn")}
          </button>
          <button
            onClick={() => navigate("/subscriptions")}
            className="px-4 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 transition-all duration-200 border hover:shadow-md hover:scale-105 cursor-pointer"
            style={{
              backgroundColor: "#EBF5FF",
              borderColor: "#A4CAFE",
              color: "#1A55DB",
            }}
          >
            {tCommon("subscriptions")}
          </button>
        </div>
      )}

      {/* App-specific modals */}
      {renderModals?.()}
    </Navbar>
  );
};

export default MainNavbar;
